import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Mail, MapPin, Send } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { STOCK, CANDIDATE, FORMSPREE_ENDPOINT, CONTACT_EMAIL } from "../lib/config";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (!form.name.trim() || !emailOk || !form.message.trim()) {
      setError("Please enter your name, a valid email, and a message.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: form.message,
          _subject: `New campaign message from ${form.name}`,
        }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(
          data?.errors?.[0]?.message ||
            "Something went wrong sending your message. Please try again."
        );
      }
    } catch (err) {
      setError("Network error — please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Get in Touch"
        title="Contact the campaign"
        intro="Have a question, an idea, or want to get involved? Send a note and the campaign team will follow up."
        testid="contact-hero"
      />

      <section className="py-20 md:py-28 bg-background">
        <div className="max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
          {/* Info */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="overflow-hidden clip-frame border border-slate-200 mb-8">
                <img src={STOCK.government} alt="Ontario County government building" className="w-full h-56 object-cover" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display text-3xl font-bold tracking-tight text-[hsl(var(--navy))]">
                Reach out directly
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                The campaign welcomes questions and support from residents across Ontario County.
              </p>
              <ul className="mt-8 space-y-5">
                <li className="flex items-start gap-4">
                  <span className="h-11 w-11 flex items-center justify-center bg-[hsl(var(--navy))] text-white rounded-sm">
                    <Mail size={18} />
                  </span>
                  <div>
                    <p className="text-sm uppercase tracking-widest text-slate-400 font-semibold">Email</p>
                    <a
                      href={`mailto:${CONTACT_EMAIL}`}
                      data-testid="contact-email-link"
                      className="focus-ring text-slate-700 font-medium hover:text-red-700 transition-colors break-all"
                    >
                      {CONTACT_EMAIL}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="h-11 w-11 flex items-center justify-center bg-[hsl(var(--navy))] text-white rounded-sm">
                    <MapPin size={18} />
                  </span>
                  <div>
                    <p className="text-sm uppercase tracking-widest text-slate-400 font-semibold">Serving</p>
                    <p className="text-slate-700 font-medium">Ontario County, New York</p>
                  </div>
                </li>
              </ul>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="bg-white border border-slate-200 rounded-sm p-8 md:p-10">
                <AnimatePresence mode="wait">
                  {submitted ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-center py-10"
                      data-testid="contact-success"
                    >
                      <div className="mx-auto h-16 w-16 flex items-center justify-center bg-green-50 text-green-600 rounded-full">
                        <CheckCircle2 size={34} />
                      </div>
                      <h3 className="mt-6 font-display text-3xl font-bold text-[hsl(var(--navy))]">
                        Thank you, {form.name.split(" ")[0]}!
                      </h3>
                      <p className="mt-3 text-slate-600 max-w-md mx-auto">
                        Your message has been received. The {CANDIDATE.shortName} campaign team will get
                        back to you soon.
                      </p>
                      <button
                        onClick={() => {
                          setSubmitted(false);
                          setForm({ name: "", email: "", message: "" });
                        }}
                        data-testid="contact-send-another"
                        className="focus-ring mt-8 inline-flex items-center gap-2 border border-slate-300 text-[hsl(var(--navy))] font-semibold px-6 py-3 rounded-sm hover:bg-slate-50 transition-colors"
                      >
                        Send another message
                      </button>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onSubmit={handleSubmit}
                      className="space-y-6"
                      data-testid="contact-form"
                      noValidate
                    >
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                          Name
                        </label>
                        <input
                          id="name"
                          type="text"
                          value={form.name}
                          onChange={update("name")}
                          data-testid="contact-name-input"
                          className="w-full border border-slate-300 rounded-sm px-4 py-3.5 outline-none focus:ring-2 focus:ring-red-700 focus:ring-offset-2 transition-shadow"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                          Email
                        </label>
                        <input
                          id="email"
                          type="email"
                          value={form.email}
                          onChange={update("email")}
                          data-testid="contact-email-input"
                          className="w-full border border-slate-300 rounded-sm px-4 py-3.5 outline-none focus:ring-2 focus:ring-red-700 focus:ring-offset-2 transition-shadow"
                          placeholder="you@example.com"
                        />
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                          Message
                        </label>
                        <textarea
                          id="message"
                          rows={5}
                          value={form.message}
                          onChange={update("message")}
                          data-testid="contact-message-input"
                          className="w-full border border-slate-300 rounded-sm px-4 py-3.5 outline-none focus:ring-2 focus:ring-red-700 focus:ring-offset-2 transition-shadow resize-none"
                          placeholder="How can the campaign help?"
                        />
                      </div>

                      {error && (
                        <p className="text-sm text-red-700 font-medium" data-testid="contact-error">
                          {error}
                        </p>
                      )}

                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="contact-submit-btn"
                        className="focus-ring group w-full inline-flex items-center justify-center gap-2 bg-red-700 text-white font-semibold px-8 py-4 rounded-sm hover:bg-red-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {submitting ? "Sending..." : "Send Message"}
                        {!submitting && (
                          <Send size={18} className="group-hover:translate-x-0.5 transition-transform" />
                        )}
                      </button>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
